import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import VerifyMobileEmail from "../../staff-onboarding/on-boarding-form/VerifyMobileEmail";
import { registerUser } from "../../../redux/reducers/postApiSlice";
import {
  convertToYYYYMMDD,
  currentMonthCurrentDate,
} from "../../common-data/Converts";
import { usersGetKey, userUpdate } from "../../../data/ApiEndpoints";
import { useUpdateForm } from "../../../data/Queries";
import SelectDropdown from "../../components-react/SelectDropdown";
import { gender } from "../../common-data/DropdowData";
import Loader from "../../ui/Loader";

const validateForm = (formValues) => {
  let errors = {};

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const nameRegex = /^[A-Za-z\s]+$/;
  const yopmailRegex = /@yopmail\.com$/i;
  const mobileRegex = /^\d{10}$/;

  const today = moment().tz("Asia/Kolkata").startOf("day");

  if (!formValues.staffMobile || !mobileRegex.test(formValues.staffMobile)) {
    errors.staffMobile = "Mobile number must be 10 digits!";
  } else if (formValues.memberMobile === formValues.staffMobile) {
    errors.staffMobile = "Customer mobile number must be different!";
  }

  if (!formValues.memberName2 || !nameRegex.test(formValues.memberName2)) {
    errors.memberName2 = "Name should only contain letters!";
  }

  if (!formValues.staffEmail || !emailRegex.test(formValues.staffEmail)) {
    errors.staffEmail = "Enter a valid email!";
  } else if (yopmailRegex.test(formValues.staffEmail)) {
    errors.staffEmail = "Yopmail is not allowed!";
  }

  if (!formValues.memberGender2) {
    errors.memberGender2 = "Required!";
  }

  if (!formValues.memberDOB2) {
    errors.memberDOB2 = "Date of birth is required!";
  } else {
    const enteredDOB2 = moment
      .tz(formValues.memberDOB2, "Asia/Kolkata")
      .startOf("day");
    if (!enteredDOB2.isBefore(today)) {
      errors.memberDOB2 = "DOB cannot be today or in the future!";
    } else {
      const age = today.diff(enteredDOB2, "years");
      if (age < 10) {
        errors.memberDOB2 = "Member must be at least 10 years old!";
      }
    }
  }

  return errors;
};

const MemberVerifiedRegistration = ({
  userInfoData,
  formValues,
  setFormValues,
  formErrors,
  setFormErrors,
  isMemberVerified,
  setIsMemberVerified,
  assignRef,
}) => {
  const dispatch = useDispatch();

  const { loading } = useSelector((state) => state.common);
  const { mutateAsync: updateUser } = useUpdateForm({
    url: userUpdate,
    key: usersGetKey,
    token: userInfoData?.token,
  });

  // Only one input for mobile
  const onChangeInput = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === "staffMobile")
      newValue = value.replace(/\D/g, "").slice(0, 10);
    setFormValues((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleRegisterUser = async (userData, TOKEN) => {
    try {
      const actionResult = dispatch(registerUser({ userData, TOKEN }));
      const result = await actionResult;

      if (result?.payload?.status && result?.payload?.data) {
        const { name, email, gender, dob, mobile } = result?.payload?.data;

        setFormValues((prevs) => ({
          ...prevs,
          memberName2: name,
          staffMobile: mobile,
          staffEmail: email,
          memberGender2: gender ? { label: gender, value: gender } : "",
          memberDOB2: dob ? convertToYYYYMMDD(dob) : prevs.memberDOB2 || "",
          member2Info: result.payload.data,
        }));

        setIsMemberVerified(true);
      } else {
        const errorMessage = result?.payload?.message || result?.error?.message;
        toast.error(errorMessage);
      }
    } catch (error) {
      console.log("handleRegisterUser ERROR => ", error);
    }
  };

  const onClickRegisterUser = () => {
    const errors = validateForm(formValues);
    setFormErrors(errors);

    if (Object.keys(errors)?.length === 0) {
      const userData = {
        uid: formValues.member2Info?.uid || "",
        name: formValues.memberName2,
        mobile: formValues.staffMobile,
        email: formValues?.staffEmail,
        gender: formValues.memberGender2?.value,
        dob: formValues.memberDOB2 || "",

        is_web: 1,
        account_type: "member",
        otp: "otp",
        referral_code: "",
        n_token: "n_token",
      };

      handleRegisterUser(userData, userInfoData?.token);
    }
  };

  function handleBack() {
    setFormValues((prevs) => ({
      ...prevs,
      staffMobile: "",
      staffEmail: "",
      memberName2: "",
      memberGender2: "",
      memberDOB2: "",
      member2Info: "",
    }));

    setIsMemberVerified(false);
  }

  return (
    <>
      {/* OTP Modal for Email */}
      <VerifyMobileEmail
        formValues={formValues}
        setFormValues={setFormValues}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
        onChangeInput={onChangeInput}
        userInfoData={userInfoData}
        onlyEmail
      />

      {/* After both verified, show registration fields */}
      {formValues.isMobileEmailVerified ? (
        <>
          <div className="formInputBox">
            <label htmlFor="memberName2" className="formInputLabel">
              Full Name <span className="required">*</span>
            </label>

            <input
              type="text"
              name="memberName2"
              ref={assignRef("memberName2")}
              className={`formInput ${
                formErrors.memberName2 ? "formError" : ""
              }`}
              placeholder="Enter Name"
              value={formValues.memberName2}
              onChange={(e) =>
                setFormValues({ ...formValues, memberName2: e.target.value })
              }
              disabled={isMemberVerified}
            />

            <span className="error-label">{formErrors?.memberName2 || ""}</span>
          </div>

          <div className="formInputBox">
            <label htmlFor="memberGender2" className="formInputLabel">
              gender <span className="required">*</span>
            </label>

            <div
              className={`commonWrapper ${
                formErrors.memberGender2 ? "formError" : ""
              } ${isMemberVerified ? "disabled" : ""}`}
              ref={assignRef("memberGender2")}
              style={{ border: isMemberVerified ? "none" : "" }}>
              <SelectDropdown
                options={gender.slice(0, -1)}
                selectedOption={formValues.memberGender2}
                setSelectedOption={(selectedValue) =>
                  setFormValues({
                    ...formValues,
                    memberGender2: selectedValue,
                  })
                }
                name="gender"
                isDisable={isMemberVerified}
              />
            </div>

            <span className="error-label">
              {formErrors?.memberGender2 || ""}
            </span>
          </div>

          <div className="formInputBox span2">
            <label htmlFor="memberDOB2" className="formInputLabel">
              DOB <span className="required">*</span>
            </label>

            <div className="input-add-btn flexBetween">
              <input
                type="date"
                name="memberDOB2"
                ref={assignRef("memberDOB2")}
                className={`formInput ${
                  formErrors.memberDOB2 ? "formError" : ""
                } ${formValues.memberDOB2 ? "date-has-value" : ""}`}
                value={formValues.memberDOB2}
                onChange={(e) =>
                  setFormValues({ ...formValues, memberDOB2: e.target.value })
                }
                max={currentMonthCurrentDate}
                disabled={isMemberVerified}
              />

              <button
                type="button"
                className="btn btn-dark black-btn"
                style={{
                  background: isMemberVerified ? "#00712d" : "#b40b0b",
                  border: isMemberVerified
                    ? "1px solid #00712d"
                    : "1px solid #b40b0b",
                  cursor: isMemberVerified ? "not-allowed" : "pointer",
                }}
                onClick={
                  isMemberVerified ? null : () => onClickRegisterUser(false)
                }
                disabled={isMemberVerified}>
                {loading ? (
                  <Loader />
                ) : isMemberVerified ? (
                  "verified"
                ) : (
                  "Register"
                )}
              </button>

              <button
                type="button"
                className="btn btn-outline-dark black-btn"
                onClick={handleBack}>
                Clear
              </button>
            </div>

            <span className="error-label">{formErrors?.memberDOB2 || ""}</span>
          </div>
        </>
      ) : null}
    </>
  );
};

export default MemberVerifiedRegistration;
